import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import LightTooltip from 'src/common/components/styled/LightTooltip';
import { normalizeDesktopColumnLayout } from 'src/helpers/columnDistribution';
import {
  applyBreakpointConfig,
  applyDesktopColumnCount,
  breakpointConfigOptions,
  breakpointOptionLabel,
  desktopColumnCountOptions,
} from 'src/helpers/columnsLayoutControls';
import {
  normalizeColumnsLayout,
  readBreakpointRowConfig,
  resizeColumnsLayout,
} from 'src/helpers/columnsLayoutUtils';
import type { BreakpointRowConfigValue } from 'src/helpers/columnsLayoutUtils';
import { IColumnsBlock } from 'src/interfaces/PageBlock';
import { ColumnsDesktopLayout } from 'src/interfaces/PageColumn';
import ColumnDistributionPicker from 'src/pages/Dashboard/System/Pages/components/ColumnDistributionPicker';
import ColumnsLayoutToolbar from 'src/pages/Dashboard/System/Pages/components/ColumnsLayoutToolbar';

interface Props {
  layout: IColumnsBlock;
  onColumnsPatch: (patch: Partial<IColumnsBlock>) => void;
}

export default function ColumnsLayoutConfigButton({ layout: rawLayout, onColumnsPatch }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const layout = useMemo(() => {
    const normalized = normalizeColumnsLayout(rawLayout);
    if (!normalized) return rawLayout;
    return { ...rawLayout, ...normalized };
  }, [rawLayout]);

  const n = layout.columns.length;
  const showDistribution = layout.columnsPerRow!.md === n;

  const handleColumnCountChange = (targetCount: 2 | 3 | 4) => {
    if (targetCount === n) return;
    const hasContent = layout.columns.some(c => c.blocks.length > 0);
    if (targetCount < n && hasContent) {
      const ok = window.confirm(t('system.pages.changeColumnCountConfirm'));
      if (!ok) return;
    }
    const resized = resizeColumnsLayout(layout, targetCount) as IColumnsBlock;
    onColumnsPatch(applyDesktopColumnCount(resized, targetCount) as Partial<IColumnsBlock>);
  };

  const handleDistributionChange = (value: ColumnsDesktopLayout) => {
    onColumnsPatch({
      desktopColumnLayout: normalizeDesktopColumnLayout(n, value),
    });
  };

  return (
    <>
      <LightTooltip title={t('system.pages.changeColumnCount')} placement="top">
        <IconButton
          size="small"
          aria-label={t('system.pages.changeColumnCount')}
          onClick={() => setOpen(true)}
        >
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </LightTooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('system.pages.changeColumnCount')}</DialogTitle>
        <DialogContent dividers>
          <ColumnsLayoutToolbar
            layout={layout}
            onChange={next => onColumnsPatch(next as Partial<IColumnsBlock>)}
            showColumnCountTitle={false}
            showResponsiveControls
            showDesktopColumnCount
            desktopColumnCount={n as 2 | 3 | 4}
            desktopColumnCountOptions={desktopColumnCountOptions()}
            onDesktopColumnCountChange={handleColumnCountChange}
            onBreakpointConfigChange={(bp, value) =>
              onColumnsPatch(
                applyBreakpointConfig(layout, bp, value as BreakpointRowConfigValue) as Partial<IColumnsBlock>,
              )
            }
            readBreakpointConfig={bp => readBreakpointRowConfig(layout, bp)}
            breakpointConfigOptions={breakpointConfigOptions(n)}
            breakpointOptionLabel={value => breakpointOptionLabel(value, t)}
          />
          {showDistribution ? (
            <ColumnDistributionPicker
              columnCount={n}
              value={layout.desktopColumnLayout}
              onChange={handleDistributionChange}
            />
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t('base.close')}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
